import { useRouteStore } from '@/stores/routeStore'
import { useProjectStore } from '@/stores/projectStore'
import { useSnapshotStore } from '@/stores/snapshotStore'
import { useExplorerUiStore } from '@/stores/explorerUiStore'
import { useGlobalFilterStore } from '@/stores/globalFilterStore'
import { IRouteStoreSync } from '../IRouteStoreSync'
import {
  IRouteParamValidator,
  RouteValidationResult,
  ValidationError,
} from '../IRouteParamValidator'
import * as HELPERS from '@/utils/helpers'
import labels from '@/translations/labels.json'
import CONSTANTS from '@/constants/Config.constants'
import { asSnapshotReference } from '@/models'
import { Project } from '@/models'
import { useRouter } from 'vue-router'

type RedirectTarget = 'project-list' | 'explorer' | 'snapshot-management' | null

/**
 * Implementation of RouteStoreSync
 *
 * Orchestrates route validation, store loading, error handling, and navigation.
 */
export default class RouteStoreSync implements IRouteStoreSync {
  constructor(private routeParamValidator: IRouteParamValidator) {}

  async syncStoreAndRoute(): Promise<void> {
    const routeStore = useRouteStore()

    // If no projectId in route, nothing to sync
    if (routeStore.projectId === undefined) {
      return
    }

    // Validate all route parameters
    const validation = await this.routeParamValidator.validateAllRouteParams()

    // If project is invalid, redirect to project list
    if (!validation.hasValidProject) {
      console.error('Redirect to project list because if invalid project')
      this.handleValidationError(validation.projectId.error)
      await this.redirectTo('project-list')
      return
    }

    // Load project into store
    await this.loadProject(validation.projectId.data as Project)

    // Load valid data into stores
    let shouldFollowRoute = true

    if (!validation.snapshotId || validation.snapshotId?.isValid) {
      await this.loadSnapshot()
    } else if (validation.snapshotId?.error) {
      this.handleValidationError(validation.snapshotId.error)
      shouldFollowRoute = false
    }

    if (!validation.artifactId || validation.artifactId?.isValid) {
      this.loadArtifact()
    } else if (validation.artifactId?.error) {
      this.handleValidationError(validation.artifactId.error)
      shouldFollowRoute = false
    }

    if (!validation.filterId || validation.filterId?.isValid) {
      await this.loadGlobalFilter()
    } else if (validation.filterId?.error) {
      this.handleValidationError(validation.filterId.error)
      shouldFollowRoute = false
    }

    // Handle case where no specific snapshot is in route
    if (
      !validation.snapshotId &&
      routeStore.snapshotId === undefined &&
      !/^\/project\/\d+\/snapshots$/.test(routeStore.path || '')
    ) {
      const beforeCheck = shouldFollowRoute
      shouldFollowRoute = await this.handleMissingSnapshot()
      console.error(
        `Verify Snapshot problem follow was ${beforeCheck} and is now ${shouldFollowRoute}`,
      )
    }

    // Determine redirect target based on validation errors
    if (!shouldFollowRoute) {
      const redirectTarget = this.determineRedirectTarget(validation)
      if (redirectTarget) {
        console.error(`Redirect now ${redirectTarget}`)
        await this.redirectTo(redirectTarget)
      }
    }
  }

  private async loadProject(project: Project): Promise<void> {
    const projectStore = useProjectStore()
    projectStore.setCurrentProject(project)
  }

  private async loadSnapshot(): Promise<void> {
    const routeStore = useRouteStore()
    const snapshotStore = useSnapshotStore()

    if (routeStore.projectId && routeStore.snapshotId) {
      await snapshotStore.loadSnapshotById(
        parseInt(routeStore.projectId),
        asSnapshotReference(routeStore.snapshotId),
      )
    }
  }

  private loadArtifact(): void {
    const routeStore = useRouteStore()
    const explorerUiStore = useExplorerUiStore()

    if (routeStore.artifactId) {
      explorerUiStore.setSelectedArtifact(routeStore.artifactId)
    }
  }

  private async loadGlobalFilter(): Promise<void> {
    const routeStore = useRouteStore()
    const globalFilterStore = useGlobalFilterStore()

    if (routeStore.projectId && routeStore.filterId) {
      await globalFilterStore.loadGlobalFiltersFromApi(
        parseInt(routeStore.projectId),
        parseInt(routeStore.filterId),
      )
    }
  }

  private async handleMissingSnapshot(): Promise<boolean> {
    const routeStore = useRouteStore()
    const snapshotStore = useSnapshotStore()

    if (!routeStore.projectId) {
      return false
    }

    const hasSnapshots = await this.routeParamValidator.checkForSnapshots(routeStore.projectId)

    if (hasSnapshots) {
      // Load snapshot list
      await snapshotStore.loadSnapshotsFromApi({
        projectId: parseInt(routeStore.projectId),
      })
      return true
    } else {
      // No snapshots found - check if we're already on snapshots management page
      if (/^\/project\/\d+\/snapshots$/.test(routeStore.path || '')) {
        return true
      }

      // Show error and redirect to overview
      HELPERS.showTopAlertMessage(true, 'error', labels.ALERT_VIEW.NO_SNAPSHOTS_FOUND)
      return false
    }
  }

  private handleValidationError(error: ValidationError | undefined): void {
    if (error) {
      const message = HELPERS.replaceVariableInString(error.message, error.value)
      HELPERS.showTopAlertMessage(true, 'error', message)
    }
  }

  private determineRedirectTarget(validation: RouteValidationResult): RedirectTarget {
    // If artifact is invalid, redirect to explorer (without artifact)
    if (validation.artifactId?.error) {
      return 'explorer'
    }

    // If snapshot or filter is invalid, redirect to snapshot management
    if (validation.snapshotId?.error || validation.filterId?.error) {
      return 'snapshot-management'
    }

    return null
  }

  private async redirectTo(target: RedirectTarget): Promise<void> {
    if (!target) return

    const routeStore = useRouteStore()
    const snapshotStore = useSnapshotStore()

    let routeName: string

    switch (target) {
      case 'project-list':
        routeName = labels.SIDEBAR.MAIN.PROJECT_LIST
        break
      case 'explorer':
        routeName = labels.SIDEBAR.MAIN.EXPLORER
        // Load snapshots for explorer
        if (routeStore.projectId) {
          await snapshotStore.loadSnapshotsFromApi({
            projectId: parseInt(routeStore.projectId),
          })
        }
        break
      case 'snapshot-management':
        routeName = labels.SIDEBAR.MAIN.SNAPSHOTS_MANAGMENT
        break
      default:
        routeName = labels.SIDEBAR.MAIN.PROJECT_LIST
    }

    // Don't redirect if we're already on the target route
    if (
      routeName === labels.SIDEBAR.MAIN.PROJECT_LIST &&
      routeStore.path === CONSTANTS.BASE_ROUTES.PROJECTLIST
    ) {
      return
    }

    useRouter().push({ name: routeName })
  }
}
